import { FC, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { FixedSizeList, ListChildComponentProps } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'

// config
import { Path } from '@/config'
// hooks
import { useAppSelector, useAppDispatch } from '@/hooks'
// features
import {
  // auth
  authSelectProfile,
  // user
  ArtistCard,
  userSelectSubscribedArtists,
  userSelectSubscribedArtistsCount,
  userSelectSubscribedArtistsFetched,
  userSelectSubscribedArtistsLength,
  userGetSubscribedArtists,
  userUtilGetArtistCardSkeletonSections,
  // like
  likeCreateUserFollow,
  likeDeleteUserFollow,
  likeSelectUserFollows,
  // subscription
  subscriptionSelectUserSubscriptions,
  subscriptionSelectUserSubscriptionsAtPeriodEnd,
  subscriptionOpenSubscriptionCreateModal,
  subscriptionOpenSubscriptionRenewModal,
  subscriptionOpenSubscriptionCancelModal,
} from '@/features'
// components
import {
  ContentLayoutVirtualized,
  ContentPlaceholder,
  SkeletonVertical,
} from '@/components'
// icons
import { icons } from '@/assets'
// styles
import { contentLayout } from '@/styles'

const Row: FC<ListChildComponentProps<any>> = memo(({ index, style }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const profile = useAppSelector(authSelectProfile)
  const userFollows = useAppSelector(likeSelectUserFollows)
  const userSubscriptions = useAppSelector(subscriptionSelectUserSubscriptions)
  const userSubscriptionsAtPeriodEnd = useAppSelector(
    subscriptionSelectUserSubscriptionsAtPeriodEnd,
  )
  const artists = useAppSelector(userSelectSubscribedArtists)
  const artist = artists[index]

  // if artist not fetched
  if (!artist) {
    return (
      <div style={style}>
        <SkeletonVertical
          containerWidth="140px"
          sections={userUtilGetArtistCardSkeletonSections(
            'artist-list-library-subscribed',
          )}
        />
      </div>
    )
  }

  const { id: profileUserId } = profile ?? {}
  const { id: artistUserId } = artist

  const isCurrentUser = profileUserId === artistUserId
  const navigateToArtistPage = () => navigate(`${Path.User}/${artistUserId}`)

  // if is current user
  if (isCurrentUser) {
    return (
      <div style={style}>
        <ArtistCard
          artist={artist}
          onNameClick={navigateToArtistPage}
          onUsernameClick={navigateToArtistPage}
        />
      </div>
    )
  }

  const isUserFollowed = userFollows[artistUserId || '']
  const isCurrentUserSubscribedToUser = userSubscriptions[artistUserId || '']
  const isCurrentUserSubscribedToUserAtPeriodEnd =
    userSubscriptionsAtPeriodEnd[artistUserId || '']

  const onFollowClick = () => dispatch(likeCreateUserFollow({ user: artist }))
  const onUnfollowClick = () => dispatch(likeDeleteUserFollow({ user: artist }))
  const onCreateSubscriptionClick = () =>
    dispatch(subscriptionOpenSubscriptionCreateModal({ artist }))
  const onRenewSubscriptionClick = () =>
    dispatch(subscriptionOpenSubscriptionRenewModal({ artist }))
  const onCancelSubscriptionClick = () =>
    dispatch(subscriptionOpenSubscriptionCancelModal({ artist }))

  return (
    <div style={style}>
      <ArtistCard
        artist={artist}
        artistActions={{
          isFollowed: isUserFollowed,
          isSubscribed: isCurrentUserSubscribedToUser,
          isSubscribedAtPeriodEnd: isCurrentUserSubscribedToUserAtPeriodEnd,

          onFollowClick,
          onUnfollowClick,
          onCreateSubscriptionClick,
          onRenewSubscriptionClick,
          onCancelSubscriptionClick,
        }}
        onNameClick={navigateToArtistPage}
        onUsernameClick={navigateToArtistPage}
      />
    </div>
  )
})

type ArtistListLibrarySubscribedProps = {}

export const ArtistListLibrarySubscribed: FC<ArtistListLibrarySubscribedProps> = () => {
  const dispatch = useAppDispatch()
  const subscribedArtists = useAppSelector(userSelectSubscribedArtists)
  const subscribedArtistsCount = useAppSelector(userSelectSubscribedArtistsCount)
  const subscribedArtistsLength = useAppSelector(userSelectSubscribedArtistsLength)
  const subscribedArtistsFetched = useAppSelector(userSelectSubscribedArtistsFetched)

  const artistBlockWidth = 140
  const artistBlockMarginRight = 20
  const itemWidth = artistBlockWidth + artistBlockMarginRight

  const artistBlockHeight = 242
  const artistBlockMarginBottom = 0
  const itemHeight = artistBlockHeight + artistBlockMarginBottom

  const defaultContentCount = 1000
  const defaultListWidth = 2238
  const itemsToFetch = 30 // min number of rows to be loaded at a time
  const itemThreshold = 15 // start fetching data when user scrolls within 15 rows

  const isItemLoaded = (index: number) => !!subscribedArtists[index]

  const loadMoreItems = (startIndex: number): void => {
    // (startIndex: number, stopIndex: number): void
    dispatch(userGetSubscribedArtists({ limit: itemsToFetch, offset: startIndex }))
  }

  // if subscribed artists are fetched but length is 0
  if (subscribedArtistsFetched && subscribedArtistsLength === 0) {
    return (
      <ContentLayoutVirtualized
        title="Subscribed Artists"
        overrideCss={contentLayout.library}
        itemWidth={itemWidth}
        itemsCount={subscribedArtistsCount}
      >
        <ContentPlaceholder
          icon={icons.HeartBroken}
          height="242px"
          title="You haven't subscribed to any artist yet"
        />
      </ContentLayoutVirtualized>
    )
  }

  return (
    <ContentLayoutVirtualized
      title="Subscribed Artists"
      overrideCss={contentLayout.library}
      itemWidth={itemWidth}
      itemsCount={subscribedArtistsCount}
    >
      <InfiniteLoader
        isItemLoaded={isItemLoaded}
        loadMoreItems={loadMoreItems}
        itemCount={subscribedArtistsCount || defaultContentCount}
        minimumBatchSize={itemsToFetch}
        threshold={itemThreshold}
      >
        {({ onItemsRendered, ref }) => (
          <FixedSizeList
            className="virtualized-list"
            height={itemHeight}
            itemSize={itemWidth}
            itemCount={subscribedArtistsCount || defaultContentCount}
            onItemsRendered={onItemsRendered}
            ref={ref}
            width={defaultListWidth}
            layout="horizontal"
          >
            {Row}
          </FixedSizeList>
        )}
      </InfiniteLoader>
    </ContentLayoutVirtualized>
  )
}

ArtistListLibrarySubscribed.propTypes = {}
